import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@/lib/components/ui/Icon/Icon";
import { useNotificationsContext } from "@/lib/context/NotificationsProvider/hooks/useNotificationsContext";
import { useAxios } from "@/lib/hooks";

import { NotificationCard } from "./NotificationCard/NotificationCard";
import styles from "./NotificationsPanel.module.scss";

export const NotificationsPanel = (): JSX.Element => {
  const { t } = useTranslation("translation");
  const { bulkNotifications, updateNotifications, setIsVisible } =
    useNotificationsContext();
  const { axiosInstance } = useAxios();

  const deleteAllNotifications = async () => {
    try {
      const ids = bulkNotifications.flatMap((bulk) =>
        bulk.notifications.map((n) => n.id),
      );
      await Promise.all(
        ids.map((id) => axiosInstance.delete(`/notifications/${id}`)),
      );
      await updateNotifications();
    } catch (error) {
      console.error("Failed to delete notifications:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const panel = document.getElementById("notifications-panel");
      const button = document.getElementById("notifications-button");

      if (!panel || !button) {
        return;
      }

      if (!panel.contains(target) && !button.contains(target)) {
        setIsVisible(false);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, [setIsVisible]);

  return (
    <div id="notifications-panel" className={styles.notifications_wrapper}>
      {bulkNotifications.length > 0 && (
        <div
          className={styles.header}
          onClick={() => void deleteAllNotifications()}
        >
          <span className={styles.delete_label}>{t("deleteAll")}</span>
          <span className={styles.delete_icon}>
            <Icon name="delete" size="small" color="grey" />
          </span>
        </div>
      )}

      {bulkNotifications.length === 0 ? (
        <span className={styles.empty}>{t("youHaveNoNotifications")}</span>
      ) : (
        <div className={styles.cards}>
          {bulkNotifications.map((notification) => (
            <NotificationCard
              key={notification.bulk_id}
              bulkNotification={notification}
            />
          ))}
        </div>
      )}
    </div>
  );
};
